import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MANAGEMENT_PAGES, ManagementKey } from "../lib/managementPages";
import styles from "../styles/managementheader.module.css";

/**
 * ManagementHeader — ported from claude.ai/design `nav-pc.js` (lp-header).
 * Shared sticky PC navigation for the 4 management systems (생활/학습/운영/공간).
 * - Hides on scroll-down, returns on scroll-up (TOP_GUARD keeps it shown near top).
 * - Fixed top progress bar reflecting this page's scroll (0→100%).
 * - '4가지 관리' 드롭다운 항목은 라우팅 대신 onSelect(key)로 중앙 허브의 active를 전환.
 */

const UserIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
    </svg>
);

const ManagementHeader: React.FC<{
    current?: ManagementKey;
    onSelect?: (key: ManagementKey) => void;
    onOpenConsult?: () => void;
    /** 전역 사이트 크롬용 — 구 헤더(fixed)+페이지 스페이서 레이아웃에 맞춰 fixed로 렌더. */
    fixed?: boolean;
    /** fixed 헤더를 아래로 미는 오프셋(px) — /winter 상단 고정 띠(38px) 아래에 헤더를 둘 때 */
    topOffset?: number;
}> = ({ current, onSelect, onOpenConsult, fixed, topOffset }) => {
    const [hidden, setHidden] = useState(false);
    const [openDrop, setOpenDrop] = useState<string | null>(null);
    const openDropRef = useRef<string | null>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const setDrop = (k: string | null) => { openDropRef.current = k; setOpenDrop(k); };

    useEffect(() => {
        let lastY = window.scrollY;
        let ticking = false;
        const THRESHOLD = 8;
        const TOP_GUARD = 32;

        const onScroll = () => {
            const y = window.scrollY;
            const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
            // 진행바: 관리 허브(current 있음)는 4가지 관리 누적(각 25%), 일반 페이지는 페이지 전체 0→100%.
            const pageProg = Math.min(1, Math.max(0, y / max));
            const pct = current
                ? ((Math.max(0, MANAGEMENT_PAGES.findIndex((p) => p.key === current)) + pageProg) / MANAGEMENT_PAGES.length) * 100
                : pageProg * 100;
            if (progressRef.current) {
                progressRef.current.style.setProperty("--lp-progress", pct + "%");
            }
            // 헤더는 히어로를 지난 뒤에만 숨는다(아래로 스크롤). 그래야 하단 PC 전환 바와
            // 동시에 교대(handoff)되어 화면엔 항상 CTA가 하나만 — 빈 구간이 생기지 않는다.
            const pastHero = y > Math.max(320, window.innerHeight * 0.55);
            if (Math.abs(y - lastY) >= THRESHOLD) {
                if (y < TOP_GUARD) setHidden(false);
                else if (y > lastY && pastHero) setHidden(true);
                else if (y < lastY) setHidden(false);
                lastY = y;
            }
            // 드롭다운이 열려 있으면 스크롤 시 즉시 닫는다 (PC-6).
            if (openDropRef.current) setDrop(null);
            // 트리거를 클릭하면 포커스가 남아 :focus-within이 드롭다운을 계속 열어두므로 함께 해제한다.
            const ae = document.activeElement;
            if (ae instanceof HTMLElement && navRef.current && navRef.current.contains(ae)) ae.blur();
            ticking = false;
        };

        onScroll();
        const handler = () => {
            if (!ticking) {
                requestAnimationFrame(onScroll);
                ticking = true;
            }
        };
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, [current]);

    const isFourMgmt = !!current;
    const isCommunity = useLocation().pathname.toLowerCase().startsWith("/community");

    return (
        <>
            <div
                ref={progressRef}
                className={`${styles.progress} ${fixed ? styles.progressFixed : ""} ${hidden ? styles.progressDetached : ""}`}
                style={fixed && topOffset ? { top: (hidden ? 0 : 60) + topOffset } : undefined}
                aria-hidden="true"
            />
            <header className={`${styles.header} ${fixed ? styles.headerFixed : ""} ${hidden ? styles.headerHidden : ""}`} style={fixed && topOffset ? { top: topOffset, ...(hidden ? { transform: `translateY(calc(-100% - ${topOffset}px))` } : {}) } : undefined}>
                <div className={styles.inner}>
                    <Link className={styles.logo} to="/" aria-label="수능선배 홈으로">
                        <img src="/img/management/sunbae-logo.svg" alt="수능선배" />
                    </Link>

                    <nav ref={navRef} className={styles.nav} aria-label="주 메뉴">
                        <div className={`${styles.navItem} ${openDrop === "intro" ? styles.isOpen : ""}`} onMouseEnter={() => setDrop("intro")} onMouseLeave={() => setDrop(null)}>
                            <a className={styles.navLink} href="#">수능선배 소개</a>
                            <div className={styles.dropdown}>
                                <Link to="/ceo">대표 소개</Link>
                                <Link to="/mapmain">지점 시설 및 비용 안내</Link>
                                <Link to="/storys">수능선배 이야기</Link>
                            </div>
                        </div>

                        <div className={styles.navItem}>
                            <Link className={styles.navLink} to="/winter">윈터스쿨 모집</Link>
                        </div>

                        <div className={styles.navItem}>
                            <Link className={styles.navLink} to="/regular">대입정규반 모집</Link>
                        </div>

                        <div className={`${styles.navItem} ${openDrop === "mgmt" ? styles.isOpen : ""}`} onMouseEnter={() => setDrop("mgmt")} onMouseLeave={() => setDrop(null)}>
                            <button
                                type="button"
                                className={styles.navLink}
                                aria-current={isFourMgmt ? "page" : undefined}
                                onClick={() => onSelect?.(current ?? "life")}
                            >
                                4가지 관리
                            </button>
                            <div className={styles.dropdown}>
                                {MANAGEMENT_PAGES.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        aria-current={p.key === current ? "page" : undefined}
                                        onClick={() => onSelect?.(p.key)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={`${styles.navItem} ${openDrop === "mentor" ? styles.isOpen : ""}`} onMouseEnter={() => setDrop("mentor")} onMouseLeave={() => setDrop(null)}>
                            <a className={styles.navLink} href="#">담임멘토</a>
                            <div className={styles.dropdown}>
                                <Link to="/teachers">담임 멘토 리스트</Link>
                            </div>
                        </div>

                        <div className={styles.navItem}>
                            <Link className={styles.navLink} to="/community" aria-current={isCommunity ? "page" : undefined}>커뮤니티</Link>
                        </div>

                        <div className={`${styles.navItem} ${openDrop === "notice" ? styles.isOpen : ""}`} onMouseEnter={() => setDrop("notice")} onMouseLeave={() => setDrop(null)}>
                            <a className={styles.navLink} href="#">공지사항</a>
                            <div className={styles.dropdown}>
                                <Link to="/notification">공지</Link>
                                <Link to="/faq">자주 묻는 질문</Link>
                                <Link to="/review">합격생 이야기</Link>
                                <Link to="/mealmenu">식단표</Link>
                                <Link to="/investpage">투자 문의</Link>
                            </div>
                        </div>
                    </nav>

                    <div className={styles.actions}>
                        <button type="button" className={styles.cta} onClick={() => onOpenConsult?.()}>
                            <UserIcon />
                            <span>상담 · 대기 신청하기</span>
                        </button>
                    </div>
                </div>
            </header>
        </>
    );
};

export default ManagementHeader;
